import { useEffect, useState } from 'react';
import { OlyCell, OlyPlay, OlySequence, OlySym } from '@raketta/shared';
import { useAppStore } from '../store/useAppStore';
import { olympusApi } from '../api/olympus';
import { OlympusSymbol } from '../components/OlympusSymbol';
import { Icon } from '../components/Icon';
import { sound } from '../lib/sound';
import { telegram } from '../lib/telegram';
import { formatTon } from '../lib/format';
import { replayLink } from '../lib/appConfig';

const COLS = 6;
const ROWS = 5;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const LOWS: OlySym[] = ['l1', 'l2', 'l3', 'l4', 'l5'];

function displayGrid(): OlyCell[][] {
  return Array.from({ length: COLS }, () =>
    Array.from({ length: ROWS }, () => ({ s: LOWS[Math.floor(Math.random() * LOWS.length)] }))
  );
}

export function OlympusScreen({
  onBack,
  replay,
  shareId,
}: {
  onBack: () => void;
  replay?: OlyPlay;
  shareId?: number;
}) {
  const balance = useAppStore((s) => s.balance);
  const setBalance = useAppStore((s) => s.setBalance);
  const showToast = useAppStore((s) => s.showToast);

  const [grid, setGrid] = useState<OlyCell[][]>(() => displayGrid());
  const [winSyms, setWinSyms] = useState<Set<OlySym>>(new Set());
  const [spinning, setSpinning] = useState(false);
  const [amount, setAmount] = useState('20');
  const [winText, setWinText] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [multBadge, setMultBadge] = useState<number | null>(null);
  const [freeBadge, setFreeBadge] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(true);

  const isReplay = !!replay;
  const balanceNum = parseFloat(balance) || 0;
  const amountNum = parseFloat(amount) || 0;
  const canSpin = !spinning && amountNum > 0 && amountNum <= balanceNum;

  // Меняем только символы — анимируется лишь изменившаяся ячейка (ключ по символу).
  const dropGrid = (g: OlyCell[][]) => setGrid(g);

  const showSequence = async (seq: OlySequence, accMult: number | null, fast: boolean) => {
    const dropMs = fast ? 240 : 360;
    const winMs = fast ? 480 : 660;
    const tumbleMs = fast ? 150 : 210;

    if (seq.steps.length === 0) {
      dropGrid(seq.finalGrid);
      setWinSyms(new Set());
      await sleep(dropMs);
      return;
    }

    for (let i = 0; i < seq.steps.length; i++) {
      const step = seq.steps[i];
      dropGrid(step.grid);
      setWinSyms(new Set());
      setStatus(i === 0 ? 'Спин' : 'Каскад');
      await sleep(dropMs);

      if (step.wins.length > 0) {
        setWinSyms(new Set(step.wins.map((w) => w.sym)));
        setStatus('Выигрыш!');
        sound.cascade(i);
        sound.win(i);
        telegram.haptic('light');
        await sleep(winMs);
        setWinSyms(new Set());
      }

      const next = i + 1 < seq.steps.length ? seq.steps[i + 1].grid : seq.finalGrid;
      dropGrid(next);
      await sleep(tumbleMs);
    }

    if (seq.multSum > 0) {
      const total = accMult != null ? accMult : seq.multSum;
      setMultBadge(total);
      setStatus(`Орбы ×${total}`);
      sound.coin();
      telegram.haptic('medium');
      await sleep(fast ? 600 : 780);
      setMultBadge(null);
    }
  };

  const animate = async (play: OlyPlay) => {
    setStatus('Спин');
    dropGrid(play.baseGrid);
    await sleep(420);
    await showSequence(play.base, null, false);

    if (play.freeSpinsAwarded > 0) {
      sound.freeSpins();
      setStatus('Фриспины!');
      setFreeBadge(`ФРИСПИНЫ ×${play.freeSpinsAwarded}`);
      await sleep(950);
      for (let i = 0; i < play.free.length; i++) {
        setFreeBadge(`ФРИСПИН ${i + 1}/${play.free.length} · ×${play.free[i].accMult}`);
        await sleep(180);
        await showSequence(play.free[i].sequence, play.free[i].accMult, true);
      }
      setFreeBadge(null);
    }
    setStatus(null);
  };

  // Режим повтора: автопроигрыш переданного спина без ставки.
  useEffect(() => {
    if (!replay) return;
    let cancelled = false;
    (async () => {
      sound.resume();
      sound.spin();
      setSpinning(true);
      setWinText(null);
      setMultBadge(null);
      setFreeBadge(null);
      setWinSyms(new Set());
      dropGrid(displayGrid());
      await sleep(420);
      if (cancelled) return;
      await animate(replay);
      if (cancelled) return;
      if (replay.totalWin > 0) {
        setWinText(`+${formatTon(replay.totalWin)}`);
        if (replay.totalWin >= 20) sound.bigWin();
        else sound.coin();
      }
      setSpinning(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSpin = async (buy = false) => {
    const cost = buy ? amountNum * 100 : amountNum;
    if (spinning || amountNum <= 0) return;
    if (cost > balanceNum) {
      showToast('Недостаточно средств', true);
      return;
    }
    telegram.haptic(buy ? 'heavy' : 'medium');
    sound.resume();
    sound.spin();
    setSpinning(true);
    setWinText(null);
    setMultBadge(null);
    setFreeBadge(null);
    setWinSyms(new Set());
    setStatus(buy ? 'Бонус куплен!' : 'Спин');
    dropGrid(displayGrid());
    await sleep(buy ? 700 : 420);

    try {
      const res = await olympusApi.spin(amount, buy);
      await animate(res.play);

      const total = parseFloat(res.totalWin);
      setBalance(res.newBalance);
      if (total > 0) {
        setWinText(`+${formatTon(res.totalWin)}`);
        if (total >= cost * 2) sound.bigWin();
        else sound.coin();
        showToast(`Выигрыш +${formatTon(res.totalWin)}`);
        telegram.notify('success');
      } else {
        telegram.notify('error');
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Ошибка спина';
      showToast(Array.isArray(msg) ? msg[0] : msg, true);
    } finally {
      setSpinning(false);
      setStatus(null);
    }
  };
  const spin = () => runSpin(false);

  const setAmt = (v: number) => {
    const c = Math.max(0, Math.min(v, balanceNum || v));
    setAmount(c % 1 === 0 ? String(c) : c.toFixed(2));
  };

  return (
    <>
      <div className="page-head">
        <button className="icon-btn" onClick={onBack} title="Назад">
          <Icon name="back" size={18} />
        </button>
        <span className="title">{isReplay ? 'Повтор · Олимп' : 'Олимп · Гнев Богов'}</span>
        <button
          className={`icon-btn ${soundOn ? 'accent' : ''}`}
          style={{ marginLeft: 'auto' }}
          onClick={() => setSoundOn(sound.toggle())}
          title="Звук"
        >
          <Icon name="bolt" size={16} />
        </button>
      </div>

      <div className="card oly-board">
        {freeBadge && <div className="oly-free">{freeBadge}</div>}
        {status && !freeBadge && <div className="oly-status">{status}</div>}
        {multBadge != null && <div className="oly-mult">×{multBadge}</div>}
        <div className="oly-grid">
          {grid.map((col, c) => (
            <div className="oly-col" key={c}>
              {col.map((cell, r) => {
                const win = cell.s !== 'scatter' && cell.s !== 'mult' && winSyms.has(cell.s);
                return (
                  <div
                    className={`oly-cell ${win ? 'hit' : ''} ${cell.s === 'mult' ? 'orb' : ''} ${cell.s === 'scatter' ? 'scat' : ''}`}
                    key={r}
                  >
                    <OlympusSymbol key={`${cell.s}-${cell.m ?? ''}`} cell={cell} />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className={`slot-win ${winText ? 'show' : ''}`}>{winText ?? ' '}</div>
      </div>

      {isReplay ? (
        <div className="card replay-foot">
          <span className="replay-tag">ПОВТОР ВЫИГРЫША</span>
          <button
            className="btn"
            disabled={spinning}
            onClick={() =>
              telegram.share(
                `Зацени реплей: +${formatTon(replay!.totalWin)} в «Олимпе» — 1Zov`,
                shareId ? replayLink(shareId) : undefined
              )
            }
          >
            Поделиться
          </button>
          <button className="btn ghost" onClick={onBack}>К повторам</button>
        </div>
      ) : (
        <div className="card bet">
          <div className="label-row">
            <span className="label">Ставка</span>
            <span className="muted">баланс {formatTon(balance)}</span>
          </div>
          <div className="field">
            <span className="gem">◆</span>
            <input
              inputMode="decimal"
              value={amount}
              disabled={spinning}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            />
            <div className="mini-actions">
              <button onClick={() => setAmt(amountNum / 2)} disabled={spinning}>½</button>
              <button onClick={() => setAmt(amountNum * 2)} disabled={spinning}>2×</button>
              <button onClick={() => setAmt(balanceNum)} disabled={spinning}>MAX</button>
            </div>
          </div>
          <button className={`btn ${spinning ? 'loading' : ''}`} disabled={!canSpin} onClick={spin}>
            {spinning ? <span className="spin" /> : 'Крутить'}
          </button>
          <button
            className="btn buy-bonus"
            disabled={spinning || amountNum <= 0 || amountNum * 100 > balanceNum}
            onClick={() => runSpin(true)}
          >
            Купить бонус · {formatTon((amountNum * 100).toFixed(2))}
          </button>
          <p className="muted">8+ одинаковых где угодно бьют и исчезают · орбы ×= множитель · покупка бонуса = сразу 15 фриспинов</p>
        </div>
      )}
    </>
  );
}
